import { z } from "zod";

/**
 * The four provider-prefixed level fields `reasoningLevel` replaced. Nothing reads
 * them and nothing stores them: every stored row was moved onto `reasoningLevel` by
 * migration `20261013090000_model_config_reasoning_level_canonical`.
 *
 * 🔴 **THEY ARE REFUSED, NOT IGNORED.** A request body is lenient about unknown keys
 * (`packages/types/src/api/public/v1/schemas/unknown-key-strictness.ledger.ts`), so a field
 * simply removed from a contract is STRIPPED: a caller still sending
 * `reasoningEffort: "high"` would get a 200 and a model running at its default. That
 * is the silent drop #8027 closed on v1 task writes. Each name below therefore
 * parses as a 400 naming its replacement, at the field's own path.
 *
 * `null` is refused too: before the collapse an explicit `null` on a legacy field
 * CLEARED the level, so accepting it now would silently keep a level the caller
 * asked to clear. The clear is `reasoningLevel: null` on the doors that take one, or
 * omitting `reasoningLevel` on a door that replaces the config whole.
 *
 * debt: the refusal keeps the four names alive for a deprecation window — in this
 *       file, in the write boundary's refusal that reuses it, and in the prose that
 *       tells a caller each is a 400: the `reasoningLevel` doc in
 *       `packages/sdk/src/types/common.ts` and the `--help` text of `agent create`
 *       and `task execute` in `packages/cli`. The contract the CLI advertises
 *       does not list them: its generator drops a key that accepts no value.
 *       Ceiling: a client may never use these names again; the refusal is only
 *       ever a clearer error than a stripped key.
 *       Upgrade trigger: a full release cycle with no 400 carrying this message in
 *       the API logs — then delete this file, its spreads, the write boundary's
 *       refusal and those three passages of prose, and let the names strip like any
 *       other unknown key.
 */
export const RETIRED_EFFORT_LEVEL_FIELDS = [
  "thinkingLevel",
  "reasoningEffort",
  "geminiThinkingLevel",
  "kimiReasoningEffort"
] as const;

export type RetiredEffortLevelField = (typeof RETIRED_EFFORT_LEVEL_FIELDS)[number];

/** The refusal message for one retired field — one spelling, shared by both refusals. */
export const retiredEffortLevelFieldMessage = (field: RetiredEffortLevelField): string =>
  `${field} was retired: send the level as reasoningLevel. Read supportedReasoningLevels on GET /models for the values a model accepts.`;

// `.optional()` makes the KEY optional in the inferred type — a bare `z.undefined()`
// infers a REQUIRED key, which would make every typed config spell the four names out.
// It does not widen what parses: the value must still be absent, and `null` or any
// word is refused with the message.
const retired = (field: RetiredEffortLevelField) =>
  z
    .undefined({ error: retiredEffortLevelFieldMessage(field) })
    .optional()
    .describe(`Retired — refused. Send the level as reasoningLevel.`);

/**
 * Spread into every request shape that accepts a model config, beside
 * `reasoningLevel`. Each key accepts only ABSENT and refuses any value, `null`
 * included. `ModelConfigBaseShape` spreads it, so every contract built from that
 * shape refuses with no edit of its own.
 */
export const RetiredEffortLevelFieldsShape = {
  thinkingLevel: retired("thinkingLevel"),
  reasoningEffort: retired("reasoningEffort"),
  geminiThinkingLevel: retired("geminiThinkingLevel"),
  kimiReasoningEffort: retired("kimiReasoningEffort")
} satisfies Record<RetiredEffortLevelField, z.ZodType>;

/**
 * A STORED record with every retired level key removed, for a merge that spreads a
 * stored config under a caller's patch. The stored side is not the caller's statement,
 * so a retired key left on it must never reach the write boundary — which refuses one —
 * on a save that did not send it.
 */
export const withoutRetiredLevelKeys = <Stored extends Readonly<Record<string, unknown>>>(
  stored: Stored
): Omit<Stored, RetiredEffortLevelField> => {
  const retired = new Set<string>(RETIRED_EFFORT_LEVEL_FIELDS);
  // `Object.fromEntries` returns `{ [k: string]: V }`, which TypeScript cannot relate back
  // to `Omit<Stored, …>`; the filter removes exactly the omitted keys.
  return Object.fromEntries(Object.entries(stored).filter(([key]) => !retired.has(key))) as Omit<
    Stored,
    RetiredEffortLevelField
  >;
};

/**
 * The retired level keys a config carries a VALUE in — `null` included, `undefined`
 * not. The same line the Zod shape draws: an absent key and an `undefined` one are
 * one state on the wire, since JSON cannot carry `undefined`.
 */
export const retiredLevelKeysOf = (config: object): readonly RetiredEffortLevelField[] =>
  RETIRED_EFFORT_LEVEL_FIELDS.filter(
    (field) => field in config && Reflect.get(config, field) !== undefined
  );
